import React from 'react';
import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';
import { apiFetch } from '../api.js';
import { RouteContext } from '../router.jsx';
import { ThemeContext } from './ThemeProvider.jsx';

export default function Layout({ variant, children }) {
  const { navigate, path } = React.useContext(RouteContext);
  const { setTheme } = React.useContext(ThemeContext);
  const [auth, setAuth] = React.useState({ loading: variant !== 'public', me: null, error: '' });
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 961;
  });
  const closeSidebar = React.useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 961) return;
    setSidebarOpen(false);
  }, []);
  const toggleSidebar = React.useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  React.useEffect(() => {
    if (variant === 'public') return;
    const media = window.matchMedia('(min-width: 961px)');
    const handleChange = (event) => {
      if (event.matches) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleChange(media);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
    } else {
      media.addListener(handleChange);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, [variant]);

  React.useEffect(() => {
    if (variant === 'public') return;
    setSidebarOpen((open) => (open && window.innerWidth >= 961 ? open : false));
  }, [path, variant]);

  React.useEffect(() => {
    if (variant === 'public') {
      setAuth({ loading: false, me: null, error: '' });
      return;
    }
    let active = true;
    setAuth((prev) => ({ ...prev, loading: true, error: '' }));
    apiFetch('/api/me')
      .then((data) => {
        if (!active) return;
        const preferredTheme = resolveThemeFromUser(data?.user?.dark_mode);
        if (preferredTheme) {
          setTheme(preferredTheme);
        }
        setAuth({ loading: false, me: data, error: '' });
        if (variant === 'staff' && data.role !== 'admin') {
          navigate('/student');
        }
        if (variant === 'student' && data.role !== 'student') {
          navigate('/staff');
        }
      })
      .catch((err) => {
        if (!active) return;
        setAuth({ loading: false, me: null, error: err?.message || 'Login required' });
        if (variant === 'staff') {
          navigate('/login/staff?error=unauthorized');
        } else {
          navigate('/login/student?error=unauthorized');
        }
      });
    return () => {
      active = false;
    };
  }, [variant, navigate, setTheme]);

  React.useEffect(() => {
    if (variant !== 'staff' || auth.loading || !auth.me?.user) return;
    if (!canAccessStaffPath(path, auth.me.user)) {
      navigate('/staff');
    }
  }, [auth.loading, auth.me, navigate, path, variant]);

  if (variant === 'public') {
    return (
      <div className="public-shell">
        <div className="public-hero">
          <div className="public-glow" />
          <div className="public-glow-2" />
        </div>
        <main className="public-main">
          {children}
        </main>
      </div>
    );
  }

  if (auth.loading) {
    return (
      <div className={`app-shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <a href="#main-content" className="skip-link">Skip to content</a>
        <Sidebar variant={variant} user={auth.me?.user} open={sidebarOpen} onClose={closeSidebar} />
        <div className="app-body">
          <Topbar
            role={auth.me?.role}
            user={auth.me?.user}
            sidebarOpen={sidebarOpen}
            onToggleSidebar={toggleSidebar}
          />
          <main className="app-main" id="main-content">
            <div className="page">
              <p className="subtle">Loading...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Sidebar variant={variant} user={auth.me?.user} open={sidebarOpen} onClose={closeSidebar} />
      <div className="app-body">
        <Topbar
          role={auth.me?.role}
          user={auth.me?.user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
        />
        <main className="app-main" id="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

function resolveThemeFromUser(darkMode) {
  if (darkMode === null || darkMode === undefined || darkMode === '') return null;
  const numeric = Number(darkMode);
  if (Number.isNaN(numeric)) return null;
  return numeric === 1 ? 'dark' : 'light';
}

const adminOnlyPaths = [
  /^\/staff\/admins(?:\/.*)?$/,
  /^\/staff\/settings$/,
  /^\/staff\/emails(?:\/.*)?$/,
  /^\/staff\/logs$/
];

const adminOrChairPaths = [
  /^\/staff\/pages$/,
  /^\/staff\/course-list$/,
  /^\/staff\/surveys(?:\/.*)?$/
];

const adminChairOrEditorPaths = [
  /^\/staff\/students\/new$/
];

function canAccessStaffPath(path, user) {
  if (!user) return false;
  const isAdmin = user.admin_privilege === 1;
  const isChair = user.chair_privilege === 1;
  const isEditor = user.edit_privilege === 1;

  if (adminOnlyPaths.some((pattern) => pattern.test(path))) {
    return isAdmin;
  }
  if (adminOrChairPaths.some((pattern) => pattern.test(path))) {
    return isAdmin || isChair;
  }
  if (adminChairOrEditorPaths.some((pattern) => pattern.test(path))) {
    return isAdmin || isChair || isEditor;
  }
  return true;
}
